import { type ReactElement } from 'react';
import {
  FileText,
  Building2,
  Calculator,
  BarChart3,
  Bell,
  PieChart,
  FileCheck,
  Send,
  Smartphone,
  BookOpen,
  Zap,
  Shield,
} from 'lucide-react';

interface FeatureCategory {
  title: string;
  icon: typeof FileText;
  color: string;
  bgColor: string;
  features: string[];
}

const featureCategories: FeatureCategory[] = [
  {
    title: 'Buchhaltung',
    icon: BookOpen,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    features: ['Belege erfassen', 'Kassenbuch führen', 'Abschreibungen', 'Nachweise verwalten', 'Datenimport'],
  },
  {
    title: 'Rechnungen',
    icon: FileText,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    features: ['Rechnungen erstellen', 'Vorlagen nutzen', 'Kundenverwaltung', 'Mahnwesen', 'Banking-Integration'],
  },
  {
    title: 'Steuern',
    icon: Calculator,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    features: ['Umsatzsteuer', 'Einkommensteuer', 'Gewerbesteuer', 'ELSTER-Anbindung', 'Bescheinigungen'],
  },
  {
    title: 'Auswertungen',
    icon: BarChart3,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    features: ['BWA erstellen', 'Summen- & Saldenliste', 'Kennzahlen', 'EÜR', 'Dashboard'],
  },
  {
    title: 'Formulare',
    icon: FileCheck,
    color: 'text-primary-600',
    bgColor: 'bg-secondary-200',
    features: ['Stundungsantrag', 'Einspruch', 'Ratenzahlung', 'Fristverlängerung', 'Berichtigung'],
  },
  {
    title: 'Einstellungen',
    icon: Shield,
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    features: ['Steuereinstellungen', 'Rechnungsvorlagen', 'Mobile App', 'Datenimport', 'Allgemein'],
  },
];

interface HighlightFeature {
  icon: typeof FileText;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}

const highlightFeatures: HighlightFeature[] = [
  {
    icon: Zap,
    title: 'Automatische Belegerkennung',
    description: 'KI-gestützte Texterkennung erfasst Beträge, Daten und Kontakte aus Ihren Belegen automatisch.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    icon: Building2,
    title: 'Banking-Integration',
    description: 'Verbinden Sie Ihre Bankkonten und importieren Sie Transaktionen automatisch.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    icon: Send,
    title: 'ELSTER Digital',
    description: 'Steuererklärungen und Bescheinigungen direkt an das Finanzamt übermitteln.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    icon: Bell,
    title: 'Intelligentes Mahnwesen',
    description: 'Automatische Zahlungserinnerungen und Mahnungen für offene Rechnungen.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
  {
    icon: PieChart,
    title: 'BWA & Auswertungen',
    description: 'Betriebswirtschaftliche Auswertungen, Kennzahlen und Reports auf Knopfdruck.',
    color: 'text-primary-600',
    bgColor: 'bg-secondary-200',
  },
  {
    icon: Smartphone,
    title: 'Mobile App',
    description: 'Belege unterwegs erfassen und Ihre Finanzen von überall im Blick behalten.',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
  },
];

export default function Features(): ReactElement {
  return (
    <section id="funktionen" className="py-20 lg:py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}
      {/* Schlichte Trennlinie. Vorher ein Verlauf ueber Blau, Gelb und
          Gruen — drei Farben auf einem Haarstrich wirken wie Dekoration
          ohne Aussage. */}
      <div className="absolute left-0 top-0 h-px w-full bg-secondary-200" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block text-primary-600 font-semibold text-sm tracking-wider uppercase mb-4">
            Funktionen
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            Ihre komplette Buchhaltung an einem Ort
          </h2>
          <p className="text-lg text-secondary-600">
            Von der Belegerfassung über Rechnungsstellung bis zur Steuererklärung –
            Saldify bietet alle Werkzeuge, die Sie für eine professionelle Buchhaltung brauchen.
          </p>
        </div>

        {/* Highlight Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {highlightFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className="group relative bg-white rounded-2xl p-8 border border-secondary-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Icon */}
              <div
                className={`w-14 h-14 ${feature.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
              >
                <feature.icon className={`w-7 h-7 ${feature.color}`} />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-secondary-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Feature Categories */}
        <div className="bg-gradient-to-br from-secondary-50 to-primary-50 rounded-3xl p-8 lg:p-12">
          <h3 className="text-2xl lg:text-3xl font-bold text-secondary-900 text-center mb-4">
            Alle Module im Überblick
          </h3>
          <p className="text-secondary-600 text-center mb-12 max-w-2xl mx-auto">
            Saldify deckt alle Bereiche Ihrer Buchhaltung ab – übersichtlich strukturiert und einfach zu bedienen.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCategories.map((category) => (
              <div
                key={category.title}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${category.bgColor} rounded-lg flex items-center justify-center`}>
                    <category.icon className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <h4 className="font-semibold text-secondary-900">{category.title}</h4>
                </div>
                <ul className="space-y-2">
                  {category.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-secondary-600">
                      <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Eckdaten. Vorher standen hier "8h Zeitersparnis pro Woche"
            (erfunden) und "GoBD zertifiziert" — eine Software kann nicht
            GoBD-zertifiziert sein, testiert wird immer das Verfahren beim
            Anwender. Jetzt nur Aussagen, die nachpruefbar stimmen. */}
        <div className="mt-16 grid grid-cols-2 gap-8 border-t border-secondary-200 pt-16 lg:grid-cols-4">
          {[
            { wert: 'ELSTER', text: 'UStVA, EÜR, ESt und GewSt direkt übermitteln' },
            { wert: 'SKR03/04', text: 'Beide Kontenrahmen, frei wählbar' },
            { wert: 'FinTS', text: 'Bankkonten direkt anbinden' },
            { wert: 'DSGVO', text: 'Server und Entwicklung in Deutschland' },
          ].map((s) => (
            <div key={s.wert} className="text-center">
              <div className="mb-2 text-2xl font-bold text-secondary-900 lg:text-3xl">{s.wert}</div>
              <p className="text-sm text-secondary-600">{s.text}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <a
            href="#preise"
            className="btn-primary px-8 py-4 text-base shadow-lg shadow-primary-600/30 hover:shadow-primary-600/50 transition-all"
          >
            Jetzt kostenlos testen
          </a>
          <p className="text-secondary-500 text-sm mt-4">14 Tage kostenlos • Keine Kreditkarte nötig</p>
        </div>
      </div>
    </section>
  );
}
